// Library methods
import { useTranslation } from "react-i18next";
import { useContext, useMemo } from "react";
import { ResponsiveBarCanvas } from "@nivo/bar";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// Contexts
import { ReportSectionContext } from "src/contexts/reports/ReportSectionContext";

// Constants
import { DEFAULT_GAZE_SPECS } from "src/constants/enums";

type GazeGraphType = {
  gazeSpots: Array<{
    id: number;
    gazeValue: number;
    bottomDistance: number;
  }>;
  isLiveUpdate?: boolean;
};

const GazeGraph = ({ gazeSpots, isLiveUpdate = false }: GazeGraphType) => {
  const { t } = useTranslation();
  const { durationStartTime, duration } = useContext(ReportSectionContext);

  const chartSpecs = useMemo(() => DEFAULT_GAZE_SPECS, []);
  const chartKeys = useMemo(
    () => [
      {
        name: "bottomDistance",
        color: "white",
      },
      {
        name: "gazeValue",
        color: "black",
      },
    ],
    []
  );

  return (
    <Box sx={{ display: "flex", width: "100%", minWidth: 0, gap: "8px" }}>
      {/* Canvas label display */}
      {!isLiveUpdate && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              display: "flex",
              alignItems: "center",
              height: 50,
              fontSize: "14px",
            }}
          >
            {t("Fixation")}
          </Typography>
          <Typography
            sx={{
              display: "flex",
              alignItems: "center",
              height: 50,
              fontSize: "14px",
            }}
          >
            {t("Blink")}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: "1 1 auto",
          overflow: "hidden",
        }}
      >
        {/* Bar canvas display */}
        <div
          style={{
            height: isLiveUpdate ? 30 : 100,
            width: "100%",
            overflow: "hidden",
            borderTop: "2px solid #000",
            borderBottom: "2px solid #000",
          }}
        >
          <ResponsiveBarCanvas
            data={gazeSpots}
            keys={chartKeys.map((key) => key.name)}
            indexBy="id"
            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
            padding={0}
            maxValue={chartSpecs?.totalValues}
            valueScale={{ type: "linear" }}
            indexScale={{ type: "band", round: false }}
            colors={chartKeys.map((key) => key.color)}
            borderWidth={0.35}
            axisTop={null}
            axisRight={null}
            axisBottom={null}
            axisLeft={null}
            enableGridY={false}
            enableLabel={false}
            tooltip={() => null}
            legends={[]}
          />
        </div>

        {/* Duration display */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Typography sx={{ fontSize: "14px" }}>{durationStartTime}</Typography>
          <Typography sx={{ fontSize: "14px" }}>{duration}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default GazeGraph;
